import React, { useState, useEffect, useContext } from "react";
import { gql, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import ProductionReport from "../pages/report/ProductionReport";

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

function EfficiencyMonitor(props) {
    const { lastUsedPlant } = useContext(StarekContext);
    const [activeTab, setActiveTab] = useState("running");
    const [collapsed, setCollapsed] = useState(false);
    const [filters, setFilters] = useState({});
    const [range, setRange] = useState([0, 0]);

    const { data: shiftData } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
        variables: { plantId: lastUsedPlant?.id },
        skip: !lastUsedPlant?.id,
    });

    useEffect(() => {
        if (!shiftData?.shifts || !props.deviceData?.shadowDevices) return;

        const now = new Date();
        const todayStr = now.toISOString().slice(0, 10);

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);

        const currentSeconds =
            now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

        const getShiftTimes = (shift, baseDateStr) => {
            const start = new Date(baseDateStr + "T00:00:00");
            const end = new Date(baseDateStr + "T00:00:00");
            start.setSeconds(shift.start_time);
            end.setSeconds(shift.end_time);
            if (shift.end_time < shift.start_time) {
                end.setDate(end.getDate() + 1);
            }
            return { start, end };
        };

        let targetShift = null;
        let selectedDateStr = todayStr;

        const sortedShifts = [...shiftData.shifts].sort(
            (a, b) => a.start_time - b.start_time,
        );

        if (activeTab === "running") {
            targetShift = sortedShifts.find((shift) => {
                if (shift.start_time < shift.end_time) {
                    return (
                        currentSeconds >= shift.start_time &&
                        currentSeconds <= shift.end_time
                    );
                } else {
                    return (
                        currentSeconds >= shift.start_time ||
                        currentSeconds <= shift.end_time
                    );
                }
            });

            if (targetShift && targetShift.end_time < targetShift.start_time) {
                if (currentSeconds <= targetShift.end_time) {
                    selectedDateStr = yesterdayStr;
                }
            }
        } else {
            let allCompletedInstances = [];

            sortedShifts.forEach((shift) => {
                const todayTimes = getShiftTimes(shift, todayStr);
                if (todayTimes.end < now) {
                    allCompletedInstances.push({
                        shift,
                        dateStr: todayStr,
                        endTimeMs: todayTimes.end.getTime(),
                    });
                }
                const yesterdayTimes = getShiftTimes(shift, yesterdayStr);
                if (yesterdayTimes.end < now) {
                    allCompletedInstances.push({
                        shift,
                        dateStr: yesterdayStr,
                        endTimeMs: yesterdayTimes.end.getTime(),
                    });
                }
            });

            allCompletedInstances.sort((a, b) => b.endTimeMs - a.endTimeMs);

            if (allCompletedInstances.length > 0) {
                targetShift = allCompletedInstances[0].shift;
                selectedDateStr = allCompletedInstances[0].dateStr;
            }
        }

        if (targetShift) {
            let diff = targetShift.end_time - targetShift.start_time;
            if (diff < 0) diff += 86400;

            const base = new Date(selectedDateStr);
            const startDate = new Date(base);
            const endDate = new Date(base);

            startDate.setSeconds(targetShift.start_time);
            endDate.setSeconds(targetShift.end_time);

            if (targetShift.end_time < targetShift.start_time) {
                endDate.setDate(endDate.getDate() + 1);
            }
            const newFilters = {
                devices: props.deviceData.shadowDevices
                    .map((d) => d.id)
                    .join(","),
                duration: `shift_${targetShift.id}`,
                shiftDate: selectedDateStr,
                durationStart: startDate.toISOString(),
                durationEnd: endDate.toISOString(),
                durationSeconds: diff,
            };

            setFilters(newFilters);
            setRange([0, diff]);
        }
    }, [shiftData, props.deviceData, activeTab]);

    return (
        <div
            className={`card card-outline card-info ${collapsed ? "collapsed-card" : ""}`}
            style={{ height: collapsed ? "auto" : "400px" }}
        >
            <div
                className="card-header ui-sortable-handle"
                style={{ cursor: "move" }}
            >
                <h3 className="card-title">
                    <i className="fas fa-clipboard mr-1" />
                    Live Production Monitor
                </h3>
                <div className="card-tools d-flex align-items-center">
                    <ul className="nav nav-pills mb-0">
                        <li className="nav-item">
                            <a
                                className={`nav-link ${
                                    activeTab === "running" ? "active" : ""
                                }`}
                                href="#revenue-chart"
                                data-toggle="tab"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab("running");
                                }}
                            >
                                Running Shift
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${
                                    activeTab === "completed" ? "active" : ""
                                }`}
                                href="#sales-chart"
                                data-toggle="tab"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab("completed");
                                }}
                            >
                                Last Completed Shift
                            </a>
                        </li>
                    </ul>
                    <button
                        type="button"
                        className="btn btn-tool ml-2"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <i
                            className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`}
                        ></i>
                    </button>
                </div>
            </div>
            <div className={`card-body ${collapsed ? "d-none" : ""}`}>
                <ProductionReport
                    deviceData={props.deviceData}
                    filters={filters}
                    range={range}
                    hideControls={true}
                />
            </div>
            <div className={`card-footer ${collapsed ? "d-none" : ""}`}>
                <div className="tw-flex tw-justify-end">
                    <Link
                        to="/reports"
                        className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-no-underline hover:tw-underline tw-flex tw-items-center tw-gap-1"
                    >
                        See Historical Production...
                        <i className="fas fa-arrow-right tw-text-xs"></i>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default EfficiencyMonitor;
